import React from "react";
import { motion } from "framer-motion";
import { Badge } from "@/shared/components/ui/badge";
import { ArrowDownToLine, ArrowUpFromLine, Clock, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const statusConfig = {
  success: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Success" },
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
  processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: "Processing" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Failed" },
  expired: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: "Expired" },
  declined: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Declined" },
};

export default function RecentTransactions({ transactions = [], onViewAll, onTransactionClick }) {
  const { t } = useLanguage();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
      className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden"
    >
      <div className="flex items-center justify-between p-6 border-b border-slate-100">
        <h3 className="text-lg font-semibold text-slate-900">{t("recentTransactions")}</h3>
        <button 
          onClick={onViewAll}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          {t("viewAll")}
        </button>
      </div>
      
      <div className="divide-y divide-slate-100">
        {transactions.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            {t("noTransactionsYet")}
          </div>
        ) : (
          transactions.slice(0, 8).map((tx, index) => {
            const statusKey = (tx.status || '').toLowerCase();
            const status = statusConfig[statusKey] || statusConfig.pending;
            const StatusIcon = status.icon;
            
            return (
              <motion.div
                key={tx.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * index }}
                onClick={() => onTransactionClick?.(tx)}
                className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    tx.type === 'deposit' ? 'bg-blue-100' : 'bg-emerald-100'
                  }`}>
                    {tx.type === 'deposit' ? (
                      <ArrowDownToLine className={`h-5 w-5 ${tx.type === 'deposit' ? 'text-blue-600' : 'text-emerald-600'}`} />
                    ) : (
                      <ArrowUpFromLine className="h-5 w-5 text-emerald-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{tx.transaction_id}</p>
                    <p className="text-sm text-slate-500">{tx.merchant_code || 'N/A'}</p>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className={`font-semibold ${tx.type === 'deposit' ? 'text-blue-600' : 'text-emerald-600'}`}>
                    {tx.type === 'deposit' ? '+' : '-'}฿{tx.amount?.toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-400">
                    {tx.created_date ? format(new Date(tx.created_date), 'HH:mm:ss') : 'N/A'}
                  </p>
                </div>
                
                <Badge className={`${status.color} gap-1 border-0`}>
                  <StatusIcon className={`h-3 w-3 ${tx.status === 'processing' ? 'animate-spin' : ''}`} />
                  {status.label}
                </Badge>
              </motion.div>
            );
          })
        )}
      </div>
    </motion.div>
  );
}